import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { FiChevronDown, FiEdit2, FiTrash2 } from "react-icons/fi";
import { ResumoPreInscricao } from "components/ui/ResumoPreInscricao";
import { useActivePhases } from "hooks/useControlDate";
import { api } from "services/api";
import { getRotuloSemestre } from "utils/direcionadores";
import { COLECAO_INICIATIVAS } from "utils/constants";
import { ROTA_PRE_INSCRICAO } from "utils/destino-inicial";
import { montarExclusaoPreInscricao } from "utils/pre-inscricao-payload";

interface CardIniciativaELBBProps {
  entrega: any;
  index: number;
  isExpanded: boolean;
  onToggle: () => void;
  onUpdate?: () => void;
  currentUserId?: string;
}

export function CardIniciativaELBB({
  entrega,
  index,
  isExpanded,
  onToggle,
  onUpdate,
  currentUserId,
}: CardIniciativaELBBProps) {
  const phases = useActivePhases();
  const navigate = useNavigate();
  const [isDeleting, setIsDeleting] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteSuccess, setDeleteSuccess] = useState(false);
  const expandedRef = useRef<HTMLDivElement>(null);
  const modalRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isExpanded) {
      setTimeout(() => expandedRef.current?.focus(), 50);
    }
  }, [isExpanded]);

  useEffect(() => {
    if (showDeleteModal) {
      setTimeout(() => modalRef.current?.focus(), 50);
    }
  }, [showDeleteModal]);

  const dentroPeridoCadastro = phases.canAccessCadastroELBB;

  const handleEdit = () => {
    navigate(ROTA_PRE_INSCRICAO, { state: { editData: entrega } });
  };

  const handleDeleteClick = () => {
    setShowDeleteModal(true);
    setDeleteSuccess(false);
  };

  const handleDeleteConfirm = async () => {
    try {
      setIsDeleting(true);
      await api.put(`/database/${COLECAO_INICIATIVAS}`, montarExclusaoPreInscricao(entrega));
      setDeleteSuccess(true);
      setTimeout(() => {
        setShowDeleteModal(false);
        setDeleteSuccess(false);
        if (onUpdate) {
          onUpdate();
        }
      }, 2000);
    } catch (error) {
      console.error("Erro ao inativar iniciativa:", error);
      alert("Erro ao inativar a iniciativa. Tente novamente.");
      setIsDeleting(false);
    }
  };

  const handleDeleteCancel = () => {
    setShowDeleteModal(false);
    setDeleteSuccess(false);
  };
  
  return (
    <div className="border border-gray-200 rounded-lg p-6 bg-white">
      <div className="space-y-3">
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1">
            <h3 className="font-semibold text-gray-800 mb-1">
              Nome da iniciativa:
            </h3>
            <p className="text-gray-600 text-sm">{entrega.title}</p>
          </div>
          {dentroPeridoCadastro && currentUserId === entrega.owner && (
            <div className="flex gap-2 w-full sm:w-auto">
              <button
                onClick={handleEdit}
                className="flex items-center gap-1 px-3 py-1 text-sm text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded transition-colors"
                title="Editar iniciativa"
              >
                <FiEdit2 className="w-4 h-4" />
                Editar
              </button>
              <button
                onClick={handleDeleteClick}
                disabled={isDeleting}
                className="flex items-center gap-1 px-3 py-1 text-sm text-red-600 hover:text-red-700 hover:bg-red-50 rounded transition-colors disabled:opacity-50"
                title="Excluir iniciativa"
              >
                <FiTrash2 className="w-4 h-4" />
                {isDeleting ? "Excluindo..." : "Excluir"}
              </button>
            </div>
          )}
        </div>

        {!isExpanded && (
          <div className="flex flex-col sm:flex-row gap-3 sm:gap-8">
            <div>
              <h3 className="font-semibold text-gray-800 mb-1">Direcionador transversal:</h3>
              <p className="text-gray-600 text-sm">{entrega.direcionador?.nome ?? "—"}</p>
            </div>
            <div>
              <h3 className="font-semibold text-gray-800 mb-1">Em qual semestre?</h3>
              <p className="text-gray-600 text-sm">{getRotuloSemestre(entrega.semestre) ?? "—"}</p>
            </div>
          </div>
        )}

        {isExpanded && (
          <div ref={expandedRef} tabIndex={-1} className="pt-4 outline-none">
            <ResumoPreInscricao iniciativa={entrega} exibirNome={false} />
          </div>
        )}
      </div>

      <button
        onClick={onToggle}
        className="mt-4 text-blue-600 font-semibold hover:text-blue-700 transition-colors flex items-center gap-2 text-sm"
      >
        <FiChevronDown
          className={`w-4 h-4 transition-transform ${
            isExpanded ? "rotate-180" : ""
          }`}
        />
        {isExpanded ? "VER MENOS INFORMAÇÕES" : "VER MAIS INFORMAÇÕES"}
      </button>

      {/* Modal de Confirmação de Exclusão */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div ref={modalRef} tabIndex={-1} role="dialog" aria-modal="true" aria-label="Confirmar exclusão de iniciativa" className="bg-white rounded-lg p-6 max-w-md w-full mx-4 shadow-xl outline-none">
            {deleteSuccess ? (
              <>
                <h3 className="text-lg font-bold text-green-600 mb-4">
                  Sucesso!
                </h3>
                <p className="text-gray-600 mb-6">
                  Iniciativa inativada com sucesso!
                </p>
              </>
            ) : (
              <>
                <h3 className="text-lg font-bold text-gray-800 mb-4">
                  Confirmar Exclusão
                </h3>
                <p className="text-gray-600 mb-6">
                  Tem certeza que deseja inativar esta iniciativa? Esta ação não poderá ser desfeita.
                </p>
                <div className="flex justify-end gap-3">
                  <button
                    onClick={handleDeleteCancel}
                    disabled={isDeleting}
                    className="px-4 py-2 text-gray-700 bg-gray-200 rounded-lg hover:bg-gray-300 transition-colors font-semibold disabled:opacity-50"
                  >
                    Cancelar
                  </button>
                  <button
                    onClick={handleDeleteConfirm}
                    disabled={isDeleting}
                    className="px-4 py-2 text-white bg-red-600 rounded-lg hover:bg-red-700 transition-colors font-semibold disabled:opacity-50"
                  >
                    {isDeleting ? "Excluindo..." : "Confirmar Exclusão"}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
